import { SxProps, Theme } from "@mui/material";
import enterpriseSsoTokens, {
  enterpriseSsoResponsiveTokens as r,
} from "./EnterpriseSSO.tokens";
import settingsShellTokens, {
  PHONE_SM_QUERY,
  settingsShellResponsiveTokens,
} from "screens/Settings/Settings.tokens";

/** Same rhythm `SettingsLayout` puts between its own stacked cards. */
const shellPhone = settingsShellResponsiveTokens.phone;
const stackGap = {
  xs: shellPhone.contentGap.std,
  sm: settingsShellResponsiveTokens.tablet.contentGap,
};

const {
  card,
  header,
  pageSubtitle,
  primaryButton,
  table,
  chip,
  planBlocked,
  valueBox,
  attributeMapping,
} = enterpriseSsoTokens;

/* --------------------------------------------------------------- page head */

/**
 * The screen supplies its own title node (title + protocol subtitle), so it has
 * to reproduce what `SettingsLayout` gives a plain string title — including the
 * step down to the tablet's 20px.
 */
export const pageTitleSx: SxProps<Theme> = {
  color: settingsShellTokens.colors.pageTitle,
  fontSize: {
    sm: settingsShellResponsiveTokens.tablet.pageTitleFontSize,
    md: settingsShellTokens.pageTitle.fontSize,
  },
  fontWeight: settingsShellTokens.pageTitle.fontWeight,
  letterSpacing: settingsShellTokens.pageTitle.letterSpacing,
  lineHeight: settingsShellTokens.pageTitle.lineHeight,
};

export const pageSubtitleSx: SxProps<Theme> = {
  mt: pageSubtitle.marginTop,
  color: pageSubtitle.color,
  fontSize: pageSubtitle.fontSize,
  fontWeight: pageSubtitle.fontWeight,
  lineHeight: pageSubtitle.lineHeight,
};
/* --------------------------------------------------------------- card shell */

export const cardSx: SxProps<Theme> = {
  /*
    Below `md` the wrapper dissolves — the seven-column table cannot fit a
    phone or an 834 tablet, so each connector stands on the page canvas as its
    own card, the way the other settings lists do at these sizes.
  */
  bgcolor: { xs: "transparent", md: "background.paper" },
  border: {
    xs: "none",
    md: `${card.borderWidth} solid ${card.border}`,
  },
  borderRadius: { xs: 0, md: card.radius },
  boxShadow: { xs: "none", md: card.shadow },
  boxSizing: "border-box",
  overflow: { xs: "visible", md: "hidden" },
  display: { xs: "flex", md: "block" },
  flexDirection: "column",
  gap: stackGap,
  [PHONE_SM_QUERY]: { gap: shellPhone.contentGap.sm },
};

/* ------------------------------------------------------------------ header */

export const actionsRowSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: { xs: "stretch", md: "flex-end" },
  alignItems: "center",
  gap: header.gap,
  // The action is wrapped in a Tooltip span, so the row has to stretch it for
  // the button's own `width: 100%` to reach the edges.
  "& > *": { width: { xs: "100%", md: "auto" } },
};

export const primaryButtonSx: SxProps<Theme> = {
  // Full width at 44px below `md`, as on every other settings screen.
  width: { xs: "100%", md: "auto" },
  height: { xs: r.action.height, md: primaryButton.height },
  minWidth: 0,
  px: { xs: r.action.paddingX, md: primaryButton.paddingX },
  py: 0,
  borderRadius: primaryButton.radius,
  bgcolor: primaryButton.bg,
  color: primaryButton.color,
  fontSize: primaryButton.fontSize,
  fontWeight: primaryButton.fontWeight,
  lineHeight: "normal",
  textTransform: "none",
  whiteSpace: "nowrap",
  boxShadow: "none",
  flexShrink: 0,
  "&:hover": { bgcolor: primaryButton.bg, boxShadow: "none" },
  "&.Mui-disabled": {
    bgcolor: primaryButton.disabledBg,
    color: primaryButton.disabledColor,
  },
};

/* -------------------------------------------------------------- table card */

export const tableScrollSx: SxProps<Theme> = {
  overflowX: { xs: "visible", md: "auto" },
  display: { xs: "contents", md: "block" },
};

export const tableInnerSx: SxProps<Theme> = {
  minWidth: { xs: 0, md: table.minWidth },
  display: { xs: "contents", md: "block" },
};

export const columnHeaderSx: SxProps<Theme> = {
  // Seven fixed tracks; the rows become cards below `md`, so the strip goes.
  display: { xs: "none", md: "grid" },
  gridTemplateColumns: table.columns,
  px: table.gutter,
  py: table.headerPaddingY,
  bgcolor: table.headerBg,
  borderBottom: `${card.borderWidth} solid ${table.headerBorder}`,
  fontSize: table.headerFontSize,
  fontWeight: table.headerFontWeight,
  letterSpacing: table.headerLetterSpacing,
  color: table.headerColor,
  lineHeight: "normal",
};

export const rowSx = (isLast: boolean): SxProps<Theme> => ({
  display: "grid",
  gridTemplateColumns: table.columns,
  alignItems: "center",
  px: table.gutter,
  py: table.rowPaddingY,
  borderBottom: isLast
    ? "none"
    : `${card.borderWidth} solid ${table.rowDivider}`,
  fontSize: table.rowFontSize,
  color: table.rowColor,
  lineHeight: "normal",
});

export const connectorNameSx: SxProps<Theme> = {
  fontSize: table.rowFontSize,
  fontWeight: 700,
  color: table.rowColor,
  lineHeight: "normal",
};

export const connectorSubTextSx: SxProps<Theme> = {
  fontSize: table.subTextFontSize,
  color: table.subTextColor,
  lineHeight: "normal",
};

export const sourceTextSx: SxProps<Theme> = {
  fontSize: table.rowFontSize,
  color: table.rowColor,
  lineHeight: "normal",
};

export const actionButtonSx: SxProps<Theme> = {
  color: table.actionColor,
  p: 0.5,
  "&:hover": { color: table.rowColor },
};

export const emptyStateSx: SxProps<Theme> = {
  px: { xs: r.card.padding, md: table.gutter },
  py: table.emptyPaddingY,
  textAlign: "center",
  fontSize: table.emptyFontSize,
  color: table.emptyColor,
  lineHeight: "normal",
  /*
    Below `md` the wrapper card dissolves so each connector can stand on the
    canvas as its own card (see `cardSx`) — which left the empty state as loose
    text on bare canvas. Give it the same chrome a connector card would have, so
    an account with no connectors still reads as a card like every sibling list.
  */
  boxSizing: "border-box",
  bgcolor: { xs: "background.paper", md: "transparent" },
  border: { xs: `${card.borderWidth} solid ${card.border}`, md: "none" },
  borderRadius: { xs: card.radius, md: 0 },
};

/* ------------------------------------------------------------------- chips */

export const neutralChipSx: SxProps<Theme> = {
  height: chip.height,
  borderRadius: chip.radius,
  px: chip.paddingX,
  bgcolor: chip.neutralBg,
  color: chip.neutralColor,
  fontSize: chip.fontSize,
  fontWeight: chip.fontWeight,
};

const STATUS_CHIP = {
  Draft: chip.statusDraft,
  Active: chip.statusActive,
  Inactive: chip.statusInactive,
} as const;

export const statusChipSx = (
  status: keyof typeof STATUS_CHIP,
): SxProps<Theme> => ({
  height: chip.height,
  borderRadius: chip.radius,
  px: chip.paddingX,
  bgcolor: STATUS_CHIP[status].bg,
  color: STATUS_CHIP[status].color,
  fontSize: chip.fontSize,
  fontWeight: chip.fontWeight,
});

/* ------------------------------------------------------------ plan-blocked */

export const planBlockedIconSx: SxProps<Theme> = {
  width: planBlocked.iconSize,
  height: planBlocked.iconSize,
  borderRadius: planBlocked.iconRadius,
  display: "grid",
  placeItems: "center",
  bgcolor: planBlocked.iconBg,
  color: planBlocked.iconColor,
};

export const planBlockedTitleSx: SxProps<Theme> = {
  fontSize: planBlocked.titleFontSize,
  fontWeight: planBlocked.titleFontWeight,
  color: planBlocked.titleColor,
  lineHeight: "normal",
};

export const planBlockedDescSx: SxProps<Theme> = {
  fontSize: planBlocked.descFontSize,
  color: planBlocked.descColor,
  lineHeight: 1.6,
};

/* ------------------------------------------------------------- value boxes */

export const valueLabelSx: SxProps<Theme> = {
  fontSize: "12px",
  fontWeight: 600,
  color: valueBox.labelColor,
  mb: 0.25,
};

export const valueTextSx: SxProps<Theme> = {
  fontFamily: "monospace",
  fontSize: "12px",
  wordBreak: "break-all",
  flex: 1,
  bgcolor: valueBox.bg,
  border: `1px solid ${valueBox.border}`,
  borderRadius: "6px",
  px: 1.5,
  py: 0.75,
  color: valueBox.color,
};

/* ---------------------------------------------------------------- card list */

/**
 * Below `md` a connector is a card: its name and id, then the protocol, status,
 * email domains and login policy as labelled chip rows, with the overflow menu
 * trailing the heading.
 */
export const connectorCardSx: SxProps<Theme> = {
  display: "flex",
  boxSizing: "border-box",
  flexDirection: "column",
  gap: r.card.gap,
  p: r.card.padding,
  bgcolor: "background.paper",
  border: `${card.borderWidth} solid ${card.border}`,
  borderRadius: card.radius,
};

export const connectorCardHeadSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "flex-start",
  gap: r.card.headGap,
};

export const connectorCardTitleSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  fontSize: r.card.nameFontSize,
  fontWeight: 700,
  color: table.rowColor,
  lineHeight: "normal",
  wordBreak: "break-word",
};

export const connectorCardSubSx: SxProps<Theme> = {
  fontSize: r.card.subFontSize,
  color: table.subTextColor,
  lineHeight: "normal",
  wordBreak: "break-all",
};

export const connectorCardChipRowSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: r.card.chipGap,
};

export const connectorCardGroupSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: r.card.chipGap,
};

export const connectorCardLabelSx: SxProps<Theme> = {
  fontSize: r.card.labelFontSize,
  fontWeight: table.headerFontWeight,
  letterSpacing: r.card.labelLetterSpacing,
  color: table.headerColor,
  textTransform: "uppercase",
  lineHeight: "normal",
};

/* ------------------------------------------------------- attribute mapping */

export const attrMappingHeaderIconSx: SxProps<Theme> = {
  width: attributeMapping.header.iconSize,
  height: attributeMapping.header.iconSize,
  borderRadius: attributeMapping.header.iconRadius,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  bgcolor: attributeMapping.header.iconBg,
  color: attributeMapping.header.iconColor,
};

export const attrMappingBannerSx: SxProps<Theme> = {
  bgcolor: attributeMapping.banner.bg,
  border: `1px solid ${attributeMapping.banner.border}`,
  borderRadius: "10px",
  py: 0.75,
  "& .MuiAlert-message": { fontSize: "0.8125rem", color: attributeMapping.banner.text },
  "& .MuiAlert-icon": { color: attributeMapping.banner.icon },
};

export const attrMappingSectionIconSx: SxProps<Theme> = {
  fontSize: 16,
  color: attributeMapping.sectionIcon,
};

export const attrMappingRequiredChipSx: SxProps<Theme> = {
  height: 22,
  borderRadius: "999px",
  px: 1,
  bgcolor: attributeMapping.requiredChip.bg,
  color: attributeMapping.requiredChip.color,
  fontSize: "11px",
  fontWeight: 700,
};

export const attrMappingRowSx = (enabled: boolean): SxProps<Theme> => ({
  border: `1px solid ${enabled ? attributeMapping.row.borderEnabled : attributeMapping.row.border}`,
  borderRadius: attributeMapping.row.radius,
  bgcolor: enabled ? attributeMapping.row.bg : "transparent",
  overflow: "hidden",
  transition: "border-color 150ms ease, background-color 150ms ease",
});

export const attrMappingFieldIconSx = (active: boolean): SxProps<Theme> => ({
  width: 30,
  height: 30,
  borderRadius: "8px",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  bgcolor: active ? attributeMapping.row.iconActiveBg : "action.hover",
  color: active ? attributeMapping.row.iconActive : attributeMapping.row.iconRest,
});

export const attrMappingDefaultChipSx: SxProps<Theme> = {
  fontFamily: "monospace",
  fontSize: "12px",
  height: 26,
  bgcolor: attributeMapping.defaultChip.bg,
  border: `1px solid ${attributeMapping.defaultChip.border}`,
  color: attributeMapping.defaultChip.color,
};

/**
 * A dragging row keeps its own background and lifts on a shadow — react-beautiful-dnd renders it
 * with position:fixed outside the list's flow, so without an opaque background it reads as
 * transparent over whatever it passes. At rest the row itself has no chrome: the Autocomplete
 * (AttributePathList) carries the same outlined box every other themed input uses.
 */
export const attrMappingPathRowSx = (isDragging: boolean): SxProps<Theme> => ({
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  borderRadius: "8px",
  bgcolor: isDragging ? attributeMapping.pathRow.draggingBg : "transparent",
  boxShadow: isDragging ? attributeMapping.pathRow.draggingShadow : "none",
  transition: "box-shadow 150ms ease, background-color 150ms ease",
});

export const attrMappingDragHandleSx = (disabled: boolean): SxProps<Theme> => ({
  display: "grid",
  placeItems: "center",
  width: 24,
  height: 32,
  flexShrink: 0,
  borderRadius: "6px",
  color: attributeMapping.pathRow.handle,
  cursor: disabled ? "not-allowed" : "grab",
  "&:active": { cursor: disabled ? "not-allowed" : "grabbing" },
  "&:hover": { color: disabled ? undefined : attributeMapping.pathRow.handleActive },
  // Focus lands here via the drag handle's own tabIndex; make that visible for keyboard reordering.
  "&:focus-visible": {
    outline: `2px solid ${attributeMapping.pathRow.handleActive}`,
    outlineOffset: "1px",
  },
});

export const attrMappingPathIndexSx: SxProps<Theme> = {
  width: 16,
  flexShrink: 0,
  textAlign: "center",
  fontSize: "11px",
  fontWeight: 700,
  color: attributeMapping.pathRow.index,
};

export const attrMappingModeOptionSx = (selected: boolean): SxProps<Theme> => ({
  flex: 1,
  m: 0,
  alignItems: "center",
  px: 1.25,
  py: 1,
  borderRadius: "10px",
  border: `1px solid ${
    selected ? attributeMapping.modeOption.borderSelected : attributeMapping.modeOption.border
  }`,
  bgcolor: selected ? attributeMapping.modeOption.bgSelected : "transparent",
  transition: "border-color 150ms ease, background-color 150ms ease",
  "& .MuiFormControlLabel-label": { width: "100%" },
});

export const attrMappingErrorSx: SxProps<Theme> = {
  display: "block",
  mt: 0.5,
  color: attributeMapping.error,
  fontWeight: 600,
};

export const invalidFieldFocusSx: SxProps<Theme> = {
  "& .MuiOutlinedInput-root.Mui-error.Mui-focused": {
    outline: "2px solid",
    outlineColor: "formField.focusBorder",
    outlineOffset: "2px",
  },
  "&.Mui-error.Mui-focused": {
    outline: "2px solid",
    outlineColor: "formField.focusBorder",
    outlineOffset: "2px",
  },
};
